import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { createRouter as createTanStackRouter } from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";
import { routeTree } from "./routeTree.gen";
import { NotFound } from "./components/not-found";
import { DefaultErrorComponent, DefaultPendingComponent } from "./components/router-fallbacks";
import { createGlobalStateStore, unauthorizedInterceptor } from "@/lib/global-state";
import { dehydrateFeatureFlags, featureFlag, hydrateFeatureFlags } from "@/lib/feature-flags";
import { parseAppSearch, stringifyAppSearch } from "@/lib/router-search-codec";
import { createRouteScrollRestoration } from "@/lib/router-scroll";

export function getRouter() {
  // One store per getRouter() call, beside the QueryClient below and for the
  // same reason. It carries the app's three global states.
  const globalState = createGlobalStateStore();

  // The takeover (#153) fills the store's maintenance slot here, where SSR sees
  // it — an effect can't, and getServerSnapshot (lib/global-state.ts) lets
  // exactly this one state survive into SSR. The mounted takeover keeps the
  // slot in step afterwards, so a flip mid-session still lands; here the flag
  // read is the server's own resolution (or the manifest on a client boot with
  // no SSR pass behind it).
  if (featureFlag("enable_maintenance")) globalState.activate("maintenance");

  // The global interception of an expired session, and the only thing that
  // flips the session-expired overlay on. It sits at the Query layer rather
  // than in a route errorComponent because that is where a failure in a
  // *mounted* route arrives: React Query hands the error back as state, the
  // route stays mounted, and the overlay renders above it with in-progress
  // edits intact. Route-level auth (the _authed gate) still redirects to login
  // on navigation — the gate and the overlay each handle their own case.
  //
  // On the server this write is deliberately inert rather than special-cased:
  // the store is masked by getServerSnapshot (lib/global-state.ts) and then
  // discarded with the request, and Query hydration restores an errored query
  // without re-firing onError. An SSR-time expiry is the backstop's, not the
  // overlay's — there is no mounted route state to preserve before hydration.
  const activateOverlayIfUnauthorized = unauthorizedInterceptor(globalState);

  // One QueryClient per getRouter() call: per request on the server, per app
  // boot on the client — never shared across SSR requests.
  const queryClient = new QueryClient({
    queryCache: new QueryCache({ onError: activateOverlayIfUnauthorized }),
    mutationCache: new MutationCache({ onError: activateOverlayIfUnauthorized }),
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        retry: false,
        refetchOnWindowFocus: false,
      },
    },
  });

  // Restoration everywhere — a fresh navigation lands at the top, Back and
  // Forward return to where they were — but on a route that leaves scroll to
  // the browser: the spec details page (#714, @/lib/router-scroll).
  const scroll = createRouteScrollRestoration();

  const router = createTanStackRouter({
    routeTree,
    context: { queryClient, globalState },
    scrollRestoration: scroll.scrollRestoration,
    // TanStack's JSON search semantics for every param except the free-text
    // ones, which stay the literal text a person typed — the donor's plain
    // `URLSearchParams` behaviour, and the only reading under which a term like
    // `1.2300` survives its own URL (@/lib/router-search-codec).
    parseSearch: parseAppSearch,
    stringifySearch: stringifyAppSearch,
    defaultPreload: "intent",
    // Let Query own staleness: always run loaders on preload so
    // ensureQueryData decides (via its own staleTime) whether to refetch.
    defaultPreloadStaleTime: 0,
    defaultNotFoundComponent: NotFound,
    defaultErrorComponent: DefaultErrorComponent,
    defaultPendingComponent: DefaultPendingComponent,
    // Feature flags travel with the dehydrated payload so the browser reads
    // exactly what the server rendered with. They are static per-environment
    // config, not per-request state — the BFF resolves them once from its own
    // environment (@/lib/feature-flags) and this only carries that resolution
    // across the SSR boundary, which is where ADR-0002 puts the flag bootstrap.
    // Set here rather than after setupRouterSsrQueryIntegration below: that
    // integration composes with whatever dehydrate/hydrate it finds, and
    // replaces them if it runs first.
    dehydrate: dehydrateFeatureFlags,
    hydrate: hydrateFeatureFlags,
  });

  // Router↔Query SSR integration: dehydrates the QueryClient into the SSR
  // stream, hydrates it on the client, wraps the app in QueryClientProvider,
  // and intercepts redirect() thrown from query fns.
  setupRouterSsrQueryIntegration({ router, queryClient });
  scroll.attach(router);

  return router;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
